import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { useSfx } from '../../../lib/sfx/index.js';
import { mountIntroScene, mountScene, type SceneHandle } from './orbitScene.js';
import { SHIP_PIECES, type ShipPieceId } from './pieces.js';
import { prefersReducedMotion } from './motion.js';

const ART = SHIP_PIECES.map((p) => ({
  id: p.id,
  medium: p.medium,
  name: p.name,
  feature: p.feature,
}));

/** Lo que el Inicio le pide a la escena (delegado al motor montado; antes de montar, no hace nada). */
export type ShipSceneHandle = Pick<SceneHandle, 'win' | 'complete' | 'launch'>;

export interface ShipSceneProps {
  /** Las piezas ya puestas al montar (las nuevas entran después con `win`). */
  placed: readonly ShipPieceId[];
  /** Tocar un módulo: el globo lo explica y, si falta, el Inicio lleva a su actividad. */
  onPieceTap?: (id: ShipPieceId, placed: boolean) => void;
}

/**
 * La escena tocable del hangar: monta el motor imperativo (`orbitScene.ts`) una vez y expone sus
 * animaciones (`win`, `complete`, `launch`) por ref. Remontar la escena reinicia el anillo, así que
 * sólo se monta de nuevo si cambia el conjunto inicial de piezas puestas.
 */
export const ShipScene = forwardRef<ShipSceneHandle, ShipSceneProps>(function ShipScene(
  { placed, onPieceTap },
  ref,
) {
  const el = useRef<HTMLDivElement>(null);
  const handle = useRef<SceneHandle | null>(null);
  const { play } = useSfx();
  const tapRef = useRef(onPieceTap);
  tapRef.current = onPieceTap;
  const key = placed.join(',');

  useEffect(() => {
    if (!el.current) return;
    handle.current = mountScene(el.current, {
      pieces: ART,
      placed: new Set(key ? key.split(',') : []),
      play,
      reduced: prefersReducedMotion,
      onPieceTap: (id, isPlaced) => tapRef.current?.(id as ShipPieceId, isPlaced),
    });
    const node = el.current;
    return () => {
      handle.current = null;
      node.replaceChildren();
    };
  }, [key, play]);

  useImperativeHandle(
    ref,
    () => ({
      win: (id) => handle.current?.win(id) ?? Promise.resolve(),
      complete: () => handle.current?.complete(),
      launch: (layer) => handle.current?.launch(layer) ?? Promise.resolve(),
    }),
    [],
  );

  return <div ref={el} className="ship-stage" aria-label="Tu nave: tocala y girala" />;
});

/** La escena de la presentación: las 7 piezas puestas, latiendo de a una. */
export function ShipIntroScene() {
  const el = useRef<HTMLDivElement>(null);
  const { play } = useSfx();
  useEffect(() => {
    if (!el.current) return;
    mountIntroScene(el.current, { pieces: ART, play, reduced: prefersReducedMotion });
    const node = el.current;
    return () => node.replaceChildren();
  }, [play]);
  return <div ref={el} className="ship-intro-art" aria-hidden="true" />;
}
